import { useState, useMemo, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ArrowRight, ChevronLeft, ChevronRight, Search, X, Layers } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AMBITI_STATS, type AmbitoId } from '../../data/ambitiStats'
import { type ObiettivoCluster, type SchedaObiettivo } from '../../data/pianiDiZona'
import { pdzSchedaSlug } from './PdzDetailPage'

const PAGE_SIZE = 10

const PDZ_OBIETTIVO_CLUSTER_COLORS: Record<ObiettivoCluster, string> = {
  'Inclusione': '#0ea5e9',
  'Benessere sociale': '#7c3aed',
  'Sistema': '#10b981',
  Interambito: '#f59e0b',
}

const AMBITO_TO_PDZ_KEY: Record<AmbitoId, string> = {
  desio: 'Desio',
  vimercate: 'Vimercate',
  monza: 'Monza',
  seregno: 'Seregno',
  carate: 'Carate',
}

type PdzSchedaListItem = {
  itemKey: string
  gruppoId: string
  gruppoTitolo: string
  gruppoColore: string
  gruppoIcona: string
  scheda: SchedaObiettivo
}

interface Props {
  items: PdzSchedaListItem[]
  selectedAmbitoId: AmbitoId | null
  emptyLabel?: string
  detailContent: (item: PdzSchedaListItem) => React.ReactNode
}

export default function PdzDialogList({ items, selectedAmbitoId, emptyLabel, detailContent }: Props) {
  const [search, setSearch] = useState('')
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [selectedItem, setSelectedItem] = useState<PdzSchedaListItem | null>(null)

  const tags = useMemo(() => {
    const counts = new Map<string, number>()
    for (const item of items) {
      const key = item.scheda.obiettivo_cluster
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => ({
        name,
        color: PDZ_OBIETTIVO_CLUSTER_COLORS[name as ObiettivoCluster] ?? '#6366f1',
      }))
  }, [items])

  const filtered = useMemo(() => {
    const term = search.toLowerCase()
    return items.filter(item => {
      if (activeTag && item.scheda.obiettivo_cluster !== activeTag) return false
      if (term) {
        const text = `${item.scheda.titolo} ${item.scheda.sottotitolo ?? ''} ${item.gruppoTitolo}`.toLowerCase()
        if (!text.includes(term)) return false
      }
      return true
    })
  }, [items, search, activeTag])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  if (safePage !== page) setPage(safePage)

  const paged = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

  if (items.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-3)', padding: 16, textAlign: 'center', minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {emptyLabel ?? 'Nessuna scheda disponibile'}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%', minHeight: '50vh' }}>
      {/* Search */}
      <div style={{ flexShrink: 0, position: 'relative', marginBottom: 8 }}>
        <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
        <input
          type="text"
          placeholder="Cerca obiettivo…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          style={{
            width: '100%',
            padding: '7px 8px 7px 28px',
            fontSize: 12,
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: 'var(--surface-2, var(--background))',
            color: 'var(--text-1)',
            outline: 'none',
          }}
        />
      </div>

      {/* Tag filters */}
      {tags.length > 1 && (
        <div style={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          marginBottom: 10,
        }}>
          {tags.map(tag => {
            const isActive = activeTag === tag.name
            return (
              <button
                key={tag.name}
                type="button"
                onClick={() => { setActiveTag(isActive ? null : tag.name); setPage(0) }}
                style={{
                  padding: '3px 8px',
                  fontSize: 10,
                  fontWeight: 600,
                  borderRadius: 12,
                  border: `1px solid ${isActive ? tag.color : 'var(--border)'}`,
                  background: isActive ? `${tag.color}20` : 'transparent',
                  color: isActive ? tag.color : 'var(--text-2)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.4,
                }}
              >
                {tag.name}
              </button>
            )
          })}
        </div>
      )}

      {/* List */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'grid', gap: 2 }}>
        {paged.map(item => {
          const col = PDZ_OBIETTIVO_CLUSTER_COLORS[item.scheda.obiettivo_cluster] ?? '#6366f1'
          return (
            <div
              key={item.itemKey}
              onClick={() => setSelectedItem(item)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 6,
                cursor: 'pointer',
                borderLeft: `3px solid ${col}`,
                background: 'var(--surface-2, transparent)',
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)', wordBreak: 'break-word' }}>
                  {item.scheda.titolo}
                </div>
                {item.scheda.sottotitolo && (
                  <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 1, wordBreak: 'break-word' }}>
                    {item.scheda.sottotitolo}
                  </div>
                )}
              </div>
              <span style={{
                flexShrink: 0,
                padding: '2px 6px',
                fontSize: 9,
                fontWeight: 600,
                borderRadius: 8,
                background: `${col}18`,
                color: col,
                whiteSpace: 'nowrap',
              }}>
                {item.scheda.obiettivo_cluster}
              </span>
            </div>
          )
        })}
        {filtered.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: 16, textAlign: 'center' }}>
            Nessun obiettivo trovato
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--border)',
          marginTop: 8,
        }}>
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            style={{
              background: 'none', border: 'none', cursor: safePage === 0 ? 'default' : 'pointer',
              color: safePage === 0 ? 'var(--text-3)' : 'var(--text-1)', padding: 4,
              opacity: safePage === 0 ? 0.4 : 1,
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-2)' }}>
            {safePage + 1} / {totalPages}
          </span>
          <button
            type="button"
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            style={{
              background: 'none', border: 'none', cursor: safePage >= totalPages - 1 ? 'default' : 'pointer',
              color: safePage >= totalPages - 1 ? 'var(--text-3)' : 'var(--text-1)', padding: 4,
              opacity: safePage >= totalPages - 1 ? 0.4 : 1,
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Detail dialog */}
      {selectedItem && typeof document !== 'undefined' ? createPortal(
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedItem(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10020,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 'min(920px, 94vw)',
              minHeight: '60vh',
              maxHeight: '86vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              background: 'var(--tooltip-bg)',
              border: '1px solid var(--tooltip-border)',
              borderRadius: 14,
              color: 'var(--tooltip-text)',
              boxShadow: '0 14px 42px rgba(0,0,0,0.35)',
              padding: 20,
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ color: 'var(--text-0)', fontSize: 14, fontWeight: 700 }}>Dettaglio obiettivo</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Link
                  to={`/pianodizona/${(selectedAmbitoId ? AMBITO_TO_PDZ_KEY[selectedAmbitoId] : 'vimercate').toLowerCase()}/${pdzSchedaSlug(selectedItem.scheda.titolo)}`}
                  state={{ from: '/pianidizona' }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    color: 'var(--text-2)', fontSize: 12, fontWeight: 600, textDecoration: 'none',
                  }}
                >
                  Apri pagina <ArrowRight style={{ width: 14, height: 14 }} />
                </Link>
                <button
                  type="button"
                  onClick={() => setSelectedItem(null)}
                  aria-label="Chiudi"
                  style={{
                    width: 28, height: 28, borderRadius: 8,
                    border: '1px solid var(--border-md)', background: 'var(--bg-2)',
                    color: 'var(--text-2)', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  <X size={15} />
                </button>
              </div>
            </div>
            <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
              {detailContent(selectedItem)}
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}
